import React, { createContext, useState, useContext, useEffect } from "react";

// Create Context
const CartContext = createContext();

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

// Cart Provider Component
export const CartProvider = ({ children }) => {
  // Load cart from localStorage on initial load
  const loadCartFromStorage = () => {
    try {
      const savedCart = localStorage.getItem("ad-furniture-cart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Error loading cart:", error);
      return [];
    }
  };

  const [cartItems, setCartItems] = useState(loadCartFromStorage);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("ad-furniture-cart", JSON.stringify(cartItems));
    } catch (error) {
      console.error("Error saving cart:", error);
    }
  }, [cartItems]);

  // Add item to cart
  const addToCart = (product, quantity = 1) => {
    setCartItems((prevItems) => {
      // Check if item already exists in cart
      const existingItem = prevItems.find((item) => item.id === product.id);

      if (existingItem) {
        // Update quantity if item exists
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item,
        );
      } else {
        // Add new item to cart
        return [
          ...prevItems,
          {
            id: product.id,
            name: product.name,
            price: product.discountPrice || product.price,
            originalPrice: product.price,
            image: product.image,
            category: product.category,
            quantity: quantity,
            inStock: product.inStock,
          },
        ];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (productId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== productId),
    );
  };

  // Update item quantity
  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item,
      ),
    );
  };

  // Clear entire cart
  const clearCart = () => {
    if (window.confirm("Are you sure you want to clear your cart?")) {
      setCartItems([]);
      setCouponCode("");
      setCouponDiscount(0);
    }
  };

  // Apply coupon code
  const applyCoupon = (code) => {
    const validCoupons = {
      SAVE10: 10,
      SAVE20: 20,
      FURNITURE15: 15,
      WELCOME25: 25,
    };

    const discount = validCoupons[code.toUpperCase()];

    if (discount) {
      setCouponCode(code.toUpperCase());
      setCouponDiscount(discount);
      return { success: true, message: `Coupon applied! ${discount}% off` };
    } else {
      return { success: false, message: "Invalid coupon code" };
    }
  };

  // Remove coupon
  const removeCoupon = () => {
    setCouponCode("");
    setCouponDiscount(0);
  };

  // Calculate subtotal
  const getSubtotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0,
    );
  };

  // Calculate discount amount
  const getDiscountAmount = () => {
    const subtotal = getSubtotal();
    return (subtotal * couponDiscount) / 100;
  };

  // Calculate shipping charges
  const getShippingCharges = () => {
    const subtotal = getSubtotal();
    if (subtotal === 0) return 0;
    if (subtotal > 50000) return 0; // Free shipping above Rs50,000
    return 1000; // Standard shipping
  };

  // Calculate tax (GST 18%)
  const getTax = () => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const taxableAmount = subtotal - discount;
    return (taxableAmount * 18) / 100;
  };

  // Calculate grand total
  const getGrandTotal = () => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const shipping = getShippingCharges();
    const tax = getTax();
    return subtotal - discount + shipping + tax;
  };

  // Get cart count
  // const getCartCount = () => {
  //   return cartItems.reduce((count, item) => count + item.quantity, 0);
  // };

  // Check if cart is empty
  const isCartEmpty = () => {
    return cartItems.length === 0;
  };

  // Context value
  const value = {
    cartItems,
    // cartCount: getCartCount(),
    subtotal: getSubtotal(),
    discountAmount: getDiscountAmount(),
    shippingCharges: getShippingCharges(),
    tax: getTax(),
    grandTotal: getGrandTotal(),
    couponCode,
    couponDiscount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    applyCoupon,
    removeCoupon,
    isCartEmpty,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
